import React from "react";
import { useTranslation } from "react-i18next";
import { ErrorMessage, Field, useFormikContext } from "formik";
import HTMLLabel from "../../../../../component/label/HTMLLabel";
import TextError from "../../../../../component/formikComponent/TextError";
import {
  CpV3Field,
  CpV3Option,
  getCandidateProfileV3SelectedIds,
  getCandidateProfileV3SubKey,
} from "./candidateProfileV3";
import "./candidateProfileV3.scss";

type Validator = ((value: any) => string | undefined) | undefined;

// Up to this many options render open, like the screening radios; more get a bordered scroll box.
const OPEN_OPTION_LIMIT = 8;

const renderOptionList = (optionCount: number, content: React.ReactNode) => (
  optionCount > OPEN_OPTION_LIMIT
    ? <div className="card p-2 job-multi-checkbox cp-v3-options">{content}</div>
    : <div className="cp-v3-options-open">{content}</div>
);

interface CandidateProfileV3FieldsProps {
  fields: CpV3Field[];
  // The apply page's own required-field validator, so these fields feed its section status.
  requiredValidation: (value: any) => string | undefined;
}

/**
 * Renders Turbo v3 candidate profile fields inside the apply page's Formik form.
 * Must be rendered under <Formik>. The page wraps it in its "Candidate Profile" section.
 */
const CandidateProfileV3Fields = ({ fields, requiredValidation }: CandidateProfileV3FieldsProps) => {
  const { t } = useTranslation();
  const formik = useFormikContext<any>();

  const validateText = (value: any) => requiredValidation(typeof value === "string" ? value.trim() : value);

  // Sub-values start empty when their parent is picked, so Formik marks them touched on submit.
  const initSubValue = (field: CpV3Field, parentId: string) => {
    if (field.subField) {
      formik.setFieldValue(getCandidateProfileV3SubKey(field, parentId), field.subField.perParent ? [] : "");
    }
  };

  const clearSubValue = (field: CpV3Field, parentId: string) => {
    if (field.subField) {
      formik.setFieldValue(getCandidateProfileV3SubKey(field, parentId), undefined);
    }
  };

  const selectOption = (key: string, optionId: string) => {
    formik.setFieldValue(key, optionId);
    formik.setFieldTouched(key, true, false);
  };

  const toggleOption = (key: string, optionId: string, checked: boolean) => {
    const current = getCandidateProfileV3SelectedIds(formik.values[key]);
    const next = checked ? [...current, optionId] : current.filter((id) => id !== optionId);
    formik.setFieldValue(key, next);
    formik.setFieldTouched(key, true, false);
  };

  const onParentToggle = (field: CpV3Field, optionId: string, checked: boolean) => {
    toggleOption(field.key, optionId, checked);
    if (checked) {
      initSubValue(field, optionId);
    } else {
      clearSubValue(field, optionId);
    }
  };

  const onParentSelect = (field: CpV3Field, optionId: string) => {
    const previous = formik.values[field.key];
    selectOption(field.key, optionId);
    if (previous !== undefined && previous !== "" && previous !== optionId) {
      clearSubValue(field, String(previous));
    }
    initSubValue(field, optionId);
  };

  // One <Field> per key: Formik keeps only the last validator registered under a name.
  const renderRadios = (name: string, options: CpV3Option[], validate: Validator, onSelect: (optionId: string) => void) => renderOptionList(
    options.length,
    <Field name={name} validate={validate}>
      {({ field: input }: any) => options.map((option) => {
        const inputId = `${name}_${option.id}`;
        return (
          <div key={inputId} className="form-check form-check-inline">
            <input
              className="form-check-input form-check-radio"
              type="radio"
              id={inputId}
              name={input.name}
              value={option.id}
              checked={String(input.value) === option.id}
              onChange={() => onSelect(option.id)}
            />
            <label className="form-check-label" htmlFor={inputId}>
              {option.label}
            </label>
          </div>
        );
      })}
    </Field>
  );

  const renderCheckboxes = (name: string, options: CpV3Option[], validate: Validator, onToggle: (optionId: string, checked: boolean) => void) => renderOptionList(
    options.length,
    <Field name={name} validate={validate}>
      {({ field: input }: any) => {
        const selected = getCandidateProfileV3SelectedIds(input.value);
        return options.map((option) => {
          const inputId = `${name}_${option.id}`;
          return (
            <div key={inputId} className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="checkbox"
                id={inputId}
                name={input.name}
                value={option.id}
                checked={selected.includes(option.id)}
                onChange={(e) => onToggle(option.id, e.target.checked)}
              />
              <label className="form-check-label" htmlFor={inputId}>
                {option.label}
              </label>
            </div>
          );
        });
      }}
    </Field>
  );

  // Second level for each selected parent: sub-categories per category, or a level per language.
  const renderSubFields = (field: CpV3Field) => {
    const subField = field.subField;
    if (!subField) {
      return "";
    }
    const selectedParents = getCandidateProfileV3SelectedIds(formik.values[field.key]);
    return selectedParents.map((parentId) => {
      const parent = field.options.find((option) => option.id === parentId);
      const subOptions = subField.perParent
        ? subField.options.filter((option) => option.parentId === parentId)
        : subField.options;
      if (!parent || subOptions.length === 0) {
        return null;
      }
      const subKey = getCandidateProfileV3SubKey(field, parentId);
      const errorMessage = subField.perParent
        ? t("CandidateProfileSubOptionRequired", { defaultValue: "Please select at least one option for {{name}}", name: parent.label })
        : t("CandidateProfileLevelRequired", { defaultValue: "Please select a level for {{name}}", name: parent.label });
      // Turbo rejects a category without a sub-category and a language without a level,
      // so these are enforced whether or not the parent field itself is required.
      const validateSub = (value: any) => (getCandidateProfileV3SelectedIds(value).length === 0 ? errorMessage : undefined);
      return (
        <div key={subKey} className="mb-3 ps-3">
          {/* parent.label is decoded plain text, so render it as text, not through HTMLLabel. */}
          <label id={subKey} className="form-label html_label_content">
            {parent.label}
            <span className="star">{" "}*</span>
          </label>
          {subField.perParent ? (
            renderCheckboxes(subKey, subOptions, validateSub, (optionId, checked) => toggleOption(subKey, optionId, checked))
          ) : (
            renderRadios(subKey, subOptions, validateSub, (optionId) => selectOption(subKey, optionId))
          )}
          <div className="pt-1">
            <ErrorMessage name={subKey} component={TextError} />
          </div>
        </div>
      );
    });
  };

  const renderField = (field: CpV3Field) => {
    if (field.type === "text" || field.type === "textarea") {
      return (
        <div className="mb-3">
          <HTMLLabel name={field.key} className="form-label" label={field.label} required={field.required} />
          {/* HTMLLabel renders <label id={name}> with no htmlFor, so link the input to it by id. */}
          <Field
            name={field.key}
            aria-labelledby={field.key}
            className="form-control"
            {...(field.type === "textarea" ? { as: "textarea", rows: "4" } : { type: "text" })}
            validate={field.required ? validateText : undefined}
          />
          <ErrorMessage name={field.key} component={TextError} />
        </div>
      );
    }

    const validate = field.required ? requiredValidation : undefined;
    return (
      <div className="mb-3">
        <HTMLLabel name={field.key} className="form-label" label={field.label} required={field.required} />
        {field.type === "multiselect" ? (
          renderCheckboxes(field.key, field.options, validate, (optionId, checked) => onParentToggle(field, optionId, checked))
        ) : (
          renderRadios(field.key, field.options, validate, (optionId) => onParentSelect(field, optionId))
        )}
        <div className="pt-1">
          <ErrorMessage name={field.key} component={TextError} />
        </div>
        {renderSubFields(field)}
      </div>
    );
  };

  // The page's own "Candidate Profile" section supplies the heading.
  return (
    <div className="row">
      {fields.map((field) => (
        <React.Fragment key={field.key}>{renderField(field)}</React.Fragment>
      ))}
    </div>
  );
};

export default CandidateProfileV3Fields;
